// @ts-nocheck: converted from JS

import { FieldTypes } from '@/components/FormBuilder/Item.utils';
import { DateTimePicker, Number, Radio, Select, SelectLookup, Text, Textarea } from '@/components/UI/FormFields/Latest';
import ReadOnlyDatepicker from '@/components/UI/FormFields/ReadOnly/Latest/Datepicker';
import ReadOnlySelect from '@/components/UI/FormFields/ReadOnly/Latest/Select';
import ReadOnlyText from '@/components/UI/FormFields/ReadOnly/Latest/Text';
import Summary, { SummaryItem } from '@/components/UI/Summary';
import type { RequestTemplateGroups } from '@/model/Request.model';
import { type FC, type PropsWithChildren, type ReactNode, useEffect } from 'react';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';

interface FormRenderProps {
  fields?: Array<{ name: string; type: string; slug?: string }>;
  groups?: Array<RequestTemplateGroups>;
  groupContainer?: ReactNode | FC<PropsWithChildren>;
  disabled?: boolean;
  submitted?: boolean;
  studyCreate?: boolean;
  testPrefix?: string;
  invalidFields?: Set<string>;
}

const FormRender: FC<FormRenderProps> = ({
  fields,
  groups,
  groupContainer,
  disabled = false,
  submitted = false,
  studyCreate = false,
  testPrefix = 'metadata-field',
  invalidFields,
}) => {
  const { setStepReady } = useStudyCreateStep();

  useEffect(() => {
    if (studyCreate) {
      setStepReady(true);
    }
  }, [studyCreate, setStepReady]);

  const getComponent = (type, props) => {
    const testId = `${testPrefix}__${props.slug ?? type}`;
    const metadataType = studyCreate ? 'study_meta' : undefined;
    const hasError = invalidFields?.has(props.slug) ?? false;

    const componentMap = {
      [FieldTypes.text]: <Text {...props} testId={testId} metadataType={metadataType} />,
      [FieldTypes.textarea]: <Textarea {...props} testId={testId} metadataType={metadataType} />,
      [FieldTypes.select]: <Select {...props} testId={testId} metadataType={metadataType} hasError={hasError} />,
      [FieldTypes.multi_select]: (
        <Select {...props} isMulti testId={testId} metadataType={metadataType} hasError={hasError} />
      ),
      [FieldTypes.date]: <DateTimePicker {...props} testId={testId} metadataType={metadataType} />,
      [FieldTypes.radio]: <Radio {...props} testId={testId} metadataType={metadataType} />,
      [FieldTypes.lookup_select]: <SelectLookup {...props} testId={testId} metadataType={metadataType} />,
      [FieldTypes.lookup_multi_select]: <SelectLookup {...props} isMulti testId={testId} metadataType={metadataType} />,
      [FieldTypes.numeric]: <Number {...props} testId={testId} metadataType={metadataType} />,
    };
    return componentMap[type];
  };

  const getReadOnlyComponent = (type, props) => {
    const { name } = props;
    const formatMap = {
      [FieldTypes.text]: <ReadOnlyText {...props} />,
      [FieldTypes.textarea]: <ReadOnlyText {...props} />,
      [FieldTypes.select]: <ReadOnlySelect name={name} isMulti={false} />,
      [FieldTypes.multi_select]: <ReadOnlySelect name={name} isMulti={true} />,
      [FieldTypes.date]: <ReadOnlyDatepicker name={name} />,
      [FieldTypes.radio]: <ReadOnlyText {...props} />,
    };
    return formatMap[type];
  };

  const GroupContainer = groupContainer || 'div';
  return (
    <div data-test-component="FormRender" data-test-element="container" data-testid="metadata-field-group">
      {fields?.map((field) => (
        <div
          key={field.name}
          className="mb4"
          data-test-element="field-container"
          data-test-key={field.slug}
          data-testid={`metadata-field-group__${field.slug}`}
        >
          {getComponent(field.type, {
            ...field,
            disabled,
          })}
        </div>
      ))}
      {groups &&
        (submitted ? (
          <div className="mw7 w-100" data-test-element="submitted-group-container">
            {groups.map((group) => (
              <Summary key={group.name} title={group.label}>
                {group.fields.map((field) => (
                  <SummaryItem key={field.name} label={field.label}>
                    {getReadOnlyComponent(field.type, field)}
                  </SummaryItem>
                ))}
              </Summary>
            ))}
          </div>
        ) : (
          groups.map((group) => (
            <GroupContainer key={group.name}>
              <>
                {group.label && (
                  <h2 className="f4 normal lh-title mb4" data-test-element="group-label" data-test-key={group.name}>
                    {group.label}
                  </h2>
                )}
                {group.fields.map((field) => (
                  <div
                    key={field.name}
                    className="mb4"
                    data-test-element="group-field"
                    data-test-key={field.name}
                    data-testid={`group-field-${field.type}`}
                  >
                    {getComponent(field.type, {
                      ...field,
                      disabled,
                    })}
                  </div>
                ))}
              </>
            </GroupContainer>
          ))
        ))}
    </div>
  );
};

export default FormRender;
